/**
 * @lines98/client/lib/meta-state-events — тонкий pub/sub-seam «свежий server-truth меты
 * прилетел» (helper-unlock A2, AC-9; паттерн `lib/identity-events`).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-05-30 «ствол → ветки»): рейл-бейдж «есть
 * новые помощники» должен обновляться, КОГДА прилетает свежий `GET /meta/state` (boot-
 * префетч / ресинк после партии / заход на мета-экран) — БЕЗ того, чтобы рейл (app-shell)
 * сам дёргал сеть или каждый экран дублировал «обнови бейдж». ОДИН общий сигнал: producer
 * (`services/meta-prefetch` на успех) эмитит server-truth `gnomeUnlocksGranted`; subscriber
 * (`app.ts` рейл) сравнивает с last-seen (`lib/gnome-unlock-badge`) и ставит/снимает dot.
 * Это НЕ копия логики по экранам — единый seam (как `lib/identity-events`/`lib/session-revoked`).
 *
 * **server-truth цел (I-Gn4):** сигнал несёт server-truth число (granted) — клиент НЕ
 * пересчитывает открытия. Брокер без состояния; granted приходит из меты, не отсюда.
 *
 * SCOPE: `emitMetaStateGranted(granted)` (producer на успешный getMetaState) +
 * `onMetaStateGranted(cb)` (подписка, возвращает unsubscribe). NOT IN SCOPE: сам fetch
 * (`services/meta-api`/`meta-prefetch`), хранение granted (`lib/meta-snapshot`), решение
 * «виден ли бейдж» (`lib/gnome-unlock-badge.hasUnseenUnlocks`).
 *
 * Cross-references:
 *   - lib/identity-events.ts (sibling pub/sub-seam — тот же паттерн).
 *   - services/meta-prefetch.ts (producer: emit на успех getMetaState).
 *   - lib/gnome-unlock-badge.ts (subscriber-логика: granted vs last-seen).
 *   - app.ts (subscriber: ставит dot на рейл через nav-rail handle).
 */

type MetaStateGrantedListener = (gnomeUnlocksGranted: number) => void;

const listeners = new Set<MetaStateGrantedListener>();

/**
 * Подписаться на «прилетел свежий server-truth granted помощников». Возвращает unsubscribe.
 * Подписчик (рейл) сравнивает granted с last-seen и ставит/снимает бейдж.
 */
export function onMetaStateGranted(cb: MetaStateGrantedListener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Опубликовать свежий server-truth `gnomeUnlocksGranted` (producer — после успешного
 * `getMetaState`). Ошибка в одном подписчике не валит остальных (best-effort).
 */
export function emitMetaStateGranted(gnomeUnlocksGranted: number): void {
  for (const cb of [...listeners]) {
    try {
      cb(gnomeUnlocksGranted);
    } catch {
      // best-effort: один подписчик не должен ронять остальные reconcile'ы.
    }
  }
}
